import CoverPic from '../CoverPic';

import {
  Title, Column, GroupName, Line, Discount, Total, Currency, FooterMessage, Price
} from "./styles";
import dateHandlder from "../../../../../helpers/dates-hanlder";
import { connect, useDispatch } from "react-redux";
import { formatPrice } from '../../../../../helpers/price-formatter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CouponDiscount from '../CouponDiscount'
import { getUrlParamByName } from '../../../../../helpers/get-url-param';
import { DiscountDto, Group, PlanDto, Subscription } from '../../../../../types';
import constants from '../../../../../constants';
import { getSubscription } from '../../../../../store/actions/subscription-admin';

const groupTypes = {
  community: "comunidad",
  free_experience: "taller",
  experience: "taller",
  membership: "membresía",
};


const periodsUnitsTranslations = {
  "month": { singular: "mes", plural: "meses" },
  "quarter": { singular: "trimestre", plural: "trimestres" },
  "half-year": { singular: "semestre", plural: "semestres" },
  "year": { singular: "año", plural: "años" },
}

interface ComponentProps {
  group: Group
  plan: PlanDto
  subscription: Subscription
  subscriptionStates: {
    loading: boolean,
    success: boolean
    error: string | boolean
  }
  coupon?: DiscountDto
  setCoupon: React.Dispatch<React.SetStateAction<DiscountDto | undefined>>
}

const Component = ({ group, plan, subscription, subscriptionStates, coupon, setCoupon }: ComponentProps) => {
  const basePrice = plan?.price || 0;

  const appliedDiscount = useMemo(() => {
    return plan?.discounts?.find(d => d.show_in_page);
  }, [plan]);

  const appliedDiscountAmount = useMemo(() => {
    if (!appliedDiscount) return 0;

    if (appliedDiscount.type === "percentage") {
      return (appliedDiscount.amount / 100) * basePrice;
    }

    // fixed_amount
    return appliedDiscount.amount;
  }, [appliedDiscount, basePrice]);

  const couponDiscountAmount = useMemo(() => {
    if (!coupon) return 0;

    const priceAfterPlanDiscount = basePrice - appliedDiscountAmount;

    if (coupon.type === "percentage") {
      return (coupon.amount / 100) * priceAfterPlanDiscount;
    }

    // fixed_amount
    return coupon.amount;
  }, [coupon, basePrice, appliedDiscountAmount]);

  const total = useMemo(() => {
    return Math.max(basePrice - appliedDiscountAmount - couponDiscountAmount, 0);
  }, [basePrice, appliedDiscountAmount, couponDiscountAmount]);

  const translations = {
    'month': {
      singular: 'mes',
      plural: 'meses'
    },
    'year': {
      singular: 'año',
      pluarl: 'años'
    },
    'week': {
      singular: 'semana',
      plural: 'semanas'
    },
    'day': {
      singular: 'día',
      plural: 'días'
    }
  }

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
    // o con Intl si querés algo más complejo
    // return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price);
  };

  const paymentFrequencyTranslated = useMemo((): string => {
    if (!plan) return "";

    if (plan.type === "charge") return "1 sola vez";

    const unit: PlanDto["period_unit"] = plan.period_unit;
    const period = plan.period;

    if (unit === "month") {
      if (period === 1) return "mensual";
      if (period === 3) return "trimestral";
      if (period === 6) return "semestral";
      if (period === 12) return "anual";
      return `cada ${period} meses`;
    }

    if (unit === "year") {
      if (period === 1) return "anual";
      return `cada ${period} años`;
    }

    return `cada ${period} ${unit as PlanDto["period_unit"] === "month" ? "meses" : "años"}`;
  }, [plan])

  function buildDiscountPeriods(price: number, discounts: DiscountDto[]) {
    const periods: { price: number, period: number }[] = [];

    // 1. Obtener el máximo período (duración) entre los descuentos
    const maxPeriod = Math.max(...discounts.map(d => d.period));

    for (let i = 1; i <= maxPeriod; i++) {
      // Para cada periodo, calcular los descuentos activos
      const activeDiscounts = discounts.filter(d => d.period >= i);

      let currentPrice = price;

      // Aplicar todos los descuentos activos
      activeDiscounts.forEach(d => {
        if (d.type === 'percentage') {
          currentPrice -= (currentPrice * d.amount) / 100;
        } else {
          currentPrice -= d.amount;
        }
      });

      // Si el último periodo agregado tiene el mismo precio, extender el período
      const last = periods[periods.length - 1];
      const roundedPrice = parseFloat(currentPrice.toFixed(2));
      if (last && last.price === roundedPrice) {
        last.period += 1;
      } else {
        periods.push({ price: roundedPrice, period: 1 });
      }
    }

    return periods;
  }

  const getPeriodLabel = (period: number, unit: keyof typeof translations): string => {
    if (!translations[unit]) return `${period} ${unit}`;
    const label = translations[unit][period > 1 ? 'plural' : 'singular'];
    return `${period} ${label}`;
  };


  const explainText = useMemo(() => {
    console.log({ plan, paymentFrequencyTranslated, appliedDiscount, coupon })
    if (!plan) return "";

    const frequency = paymentFrequencyTranslated;
    const baseText = `Cobro ${frequency}.`;
    const autoCancel = "Se cobrará automáticamente y podés cancelar cuando quieras.";

    const priceRegular = plan.price;
    const discounts = plan.discounts || [];

    if (discounts.length === 0 && !coupon) {
      // CASO 1 – Sin descuentos
      const frequencyText =
        plan?.type === "subscription" && plan?.period && plan?.period_unit
          ? `cada ${getPeriodLabel(plan.period, plan.period_unit)}`
          : "";

      return `${baseText} Pagarás ${formatPrice(priceRegular)} ${frequencyText}. ${autoCancel}`;
    }

    // Ordenamos descuentos en el orden correcto
    const discountChain: DiscountDto[] = [];
    if (appliedDiscount) discountChain.push(appliedDiscount);
    if (coupon) discountChain.push(coupon);

    console.log('discountChain', discountChain)

    let prices = buildDiscountPeriods(priceRegular, discountChain);

    // Armamos estructura de texto final

    // CASO 5 – Cupón + descuento duran lo mismo y dan mismo precio
    if (
      prices.length === 2 &&
      prices[0].period === prices[1].period &&
      prices[0].price === prices[1].price
    ) {
      return `${baseText} Con los descuentos aplicados, pagarás ${formatPrice(prices[0].price)} durante ${prices[0].period / (plan?.period || 1)} pago${(prices[0].period / (plan?.period || 1)) > 1 ? "s" : ""}. Luego, ${formatPrice(priceRegular)}. ${autoCancel}`;
    }

    // CASO 4 – Cupón + descuento combinados
    if (prices.length === 2) {
      return `${baseText} Con los descuentos aplicados, pagarás ${formatPrice(prices[0].price)} durante ${prices[0].period / (plan?.period || 1)} pago${(prices[0].period / (plan?.period || 1)) > 1 ? "s" : ""}, luego ${formatPrice(prices[1].price)} durante ${prices[1].period / (plan?.period || 1)} pago${(prices[1].period / (plan?.period || 1)) > 1 ? "s" : ""}, y después ${formatPrice(priceRegular)}. ${autoCancel}`;
    }

    // CASO 2 o 3 – Solo un descuento
    if (prices.length === 1) {
      const d = prices[0];
      const label = coupon ? "Con el cupón aplicado" : "Por el descuento aplicado";
      return `${baseText} ${label}, pagarás ${formatPrice(d.price)} durante ${d.period / (plan?.period || 1)} pago${(d.period / (plan?.period || 1)) > 1 ? "s" : ""}. Luego, ${formatPrice(priceRegular)}. ${autoCancel}`;
    }

    return "";
  }, [plan, paymentFrequencyTranslated, appliedDiscount, coupon])

  const planPeriod = (plan?: PlanDto) => {
    if (plan) {
      if (plan.type === 'subscription') {
        const isPlural = plan?.period && plan?.period > 1;
        const period = plan?.period_unit && periodsUnitsTranslations[plan?.period_unit]
        return `Cada ${plan?.period} ${period[isPlural ? "plural" : "singular"]}`
      } else {
        return "Pago único"
      }
    }
  }

  const formatDatePlus10Days = (utcSeconds) => {
    const ts = Number(utcSeconds);

    if (!ts || isNaN(ts)) {
      console.warn("Timestamp is invalid:", utcSeconds);
      return "Invalid date";
    }

    const date = new Date(ts * 1000);
    if (isNaN(date.getTime())) {
      console.warn("Date conversion failed for:", ts);
      return "Invalid date";
    }

    date.setUTCDate(date.getUTCDate() + 10);

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    // const year = date.getUTCFullYear();

    return `${day}/${month}`;
  };

  return (
    <Column>
      <div style={{ width: '100%', maxWidth: 350 }}>
        <Title>Resumen del pedido</Title>
        <div style={{ height: 20 }} />
        <CoverPic />
        <div style={{ height: 20 }} />
        <GroupName>{group?.group_name}</GroupName>
        <div style={{ height: 20 }} />
        <CouponDiscount coupon={coupon} setCoupon={setCoupon} />
        <div style={{ height: 36 }} />
        <Line />
        <div style={{ height: 12 }} />
        <Price>Precio {!!subscription && "actual del plan"} <span>{formatPrice(plan?.price)}</span></Price>


        {!!subscription && subscription?.current_amount !== plan?.price ? (
          <>
            <div style={{ height: 14 }} />
            <Discount>Descuento si reactivas antes del {formatDatePlus10Days(subscription?.cancelled_at)} <span>- {formatPrice((plan?.price - subscription?.current_amount))}</span></Discount>
          </>
        ) : (
          <>
            {appliedDiscountAmount > 0 && (
              <>
                <div style={{ height: 14 }} />
                <Discount>Descuento promocional <span>- {formatPrice(appliedDiscountAmount)}</span></Discount>
              </>
            )}
          </>
        )}

        {!!coupon && <Discount>Cupón exclusivo ({coupon?.amount}% off) <span>- {formatPrice(couponDiscountAmount)}</span></Discount>}

        <div style={{ height: 12 }} />
        <Line />
        <div style={{ height: 20 }} />
        {!subscription ? (
          <Total>Total <span>{formatPrice(total)}<small> /{planPeriod(plan)}</small></span>
          </Total>
        ) : (
          <Total>Total <span>{formatPrice(plan?.price - (plan?.price - subscription?.current_amount))} <small>/{planPeriod(plan)}</small></span></Total>
        )}
        <div style={{ height: 20 }} />
        <Currency>Monto total a pagar en {plan?.currency}.</Currency>
        <div style={{ height: 10 }} />
        <FooterMessage>{explainText}</FooterMessage>
      </div>
    </Column>
  )
}

const state = ({ groupStore, planStore, subscriptionAdminStore }) => {
  const { data: group } = groupStore?.group;
  const { data: plan } = planStore?.onePlan;
  const { data: subscription, states: subscriptionStates } = subscriptionAdminStore.subscriptionAdmin;

  return { group, plan, subscription, subscriptionStates };
};

export default connect(state)(Component);